// src/components/suiviEnseignants/SuiviEnseignants.jsx
// ============================================================================
// Suivi des enseignants — la vue globale de la direction
// ============================================================================
//
// Une ligne par enseignant suivi (employé actif qui a, cette année, une
// matière, une classe, des cours à l'emploi du temps, ou un espace
// enseignant) : avancement de ses programmes, appels de la semaine, avis
// donnés, état de son espace. Chaque ligne mène à la page de l'enseignant
// (navigation drill-in, pas de fenêtre).
//
// Remplace l'écran « Suivi direction » (SuiviEnseignantsDirection, laissé
// mort) : la direction ne VALIDE plus un programme, elle le suit et le
// commente (décision de Daniel du 6 octobre 2026). Les programmes appartiennent
// au couple classe × matière et sont tenus par les enseignants dans leur
// espace (lot C).
//
// Tout est calculé par le serveur pour la semaine demandée : rien ici ne
// compare une date. Les « appels attendus » sont une ESTIMATION — l'école n'a
// pas de calendrier des jours sans classe : un cours prévu un jour férié compte
// comme un appel manquant. L'écran le dit.
//
// La semaine, le filtre et la recherche vivent dans l'adresse
// (`?semaine=…&filtre=…&q=…`) : revenir de la page d'un enseignant ramène à
// la même vue — un état React serait perdu, la liste étant remontée au retour.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CalendarCheck, ChevronRight, ClipboardList, MessageSquare, Search, Users } from 'lucide-react';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import InfoBulle from '../ui/InfoBulle';
import LoadingSpinner from '../LoadingSpinner';
import { useFeatures } from '../../contexts/FeaturesContext';
import { usePermissions } from '../../hooks/usePermissions';
import { suiviEnseignants } from './apiSuiviEnseignants';
import {
  BadgeEspace, BarreAvancement, CarteCompteur, ChoixSemaine, estSansEspace
} from './elementsSuivi';
// Les pastilles de filtre d'une liste : le helper de l'ecran jumeau, deja a
// l'arrondi d'un bouton (consigne du 07/10/2026).
import { classesFiltre } from '../validationNotes/presentationNotes';
import {
  EST_DATE, enPourcentage, libelleDe, nombre, nomPersonne, pluriel, sansAccents
} from './formatSuivi';

const FILTRES = [
  { cle: 'tous', libelle: 'Tous', garde: () => true },
  { cle: 'appels', libelle: 'Appels manquants', garde: (e) => nombre(e.appels?.manquants) > 0 },
  { cle: 'retard', libelle: 'Programme en retard', garde: (e) => nombre(e.programme?.en_retard) > 0 },
  { cle: 'sans_espace', libelle: 'Sans espace', garde: (e) => estSansEspace(e.espace) }
];

// ----------------------------------------------------------------------------
// L'etat d'un enseignant : ce qui met sa ligne en haut de l'ecran
// ----------------------------------------------------------------------------
//
// Les quatre cartes de compteurs disent COMBIEN ; la liste doit dire QUI. Elle
// etait triee par nom : la question du matin — qui est en retard ? — se lisait
// en parcourant cinquante-cinq lignes, ou en cliquant un filtre. Les bandes
// d'etat repondent avant le premier clic.
//
// LES SEUILS SONT UNE DECISION, PAS UN CALCUL. Ceux-ci sont ceux de la maquette
// retenue le 10 octobre 2026. Les changer, c'est changer ces deux nombres —
// rien d'autre dans le fichier n'en depend.
const SEUILS_ETAT = {
  // Au-dela de ce nombre d'appels manquants, l'enseignant passe « a reprendre »
  // plutot qu'« a surveiller ».
  appels_manquants_graves: 3
};

const ETATS_SUIVI = [
  {
    cle: 'a_reprendre',
    libelle: 'A reprendre',
    pourquoi: `aucun programme saisi, ou plus de ${SEUILS_ETAT.appels_manquants_graves} appels manquants`,
    // Rouge, ambre et vert se distinguent aussi par leur clarte, pas seulement
    // par leur teinte : la bande reste lisible pour qui confond les couleurs.
    bande: 'border-red-700 bg-red-50',
    texte: 'text-red-800',
    pastille: 'bg-red-700'
  },
  {
    cle: 'a_surveiller',
    libelle: 'A surveiller',
    pourquoi: 'un appel manquant, ou un programme en retard',
    bande: 'border-amber-600 bg-amber-50',
    texte: 'text-amber-800',
    pastille: 'bg-amber-600'
  },
  {
    cle: 'a_jour',
    libelle: 'A jour',
    pourquoi: 'programmes a jour, aucun appel manquant',
    bande: 'border-emerald-700 bg-emerald-50',
    texte: 'text-emerald-800',
    pastille: 'bg-emerald-700'
  }
];

/**
 * Dans quelle bande tombe un enseignant.
 *
 * Un enseignant sans aucune classe-matiere n'a pas de programme a tenir : il
 * n'est pas « a reprendre » pour autant, sinon la premiere bande se remplirait
 * de gens qui n'ont rien a faire.
 */
const etatSuivi = (e) => {
  const manquants = nombre(e.appels?.manquants);
  const couples = nombre(e.programme?.couples);
  const sans = nombre(e.programme?.sans_programme);
  const retard = nombre(e.programme?.en_retard);

  if (manquants > SEUILS_ETAT.appels_manquants_graves) return 'a_reprendre';
  if (couples > 0 && sans >= couples) return 'a_reprendre';
  if (manquants > 0 || retard > 0 || sans > 0) return 'a_surveiller';
  return 'a_jour';
};

const listeDe = (valeur) => (Array.isArray(valeur) ? valeur.map(libelleDe).filter(Boolean) : []);

/** Ce que dit la colonne « Programme » : le pourcentage, puis ce qui cloche. */
function resumeProgramme(programme) {
  const couples = nombre(programme?.couples);
  const sans = nombre(programme?.sans_programme);
  const retard = nombre(programme?.en_retard);
  if (!couples) return { texte: 'Aucune classe-matière', ton: 'text-gray-500', barre: false };
  if (sans >= couples) return { texte: 'Programme non saisi', ton: 'text-gray-500', barre: false };
  const morceaux = [enPourcentage(programme?.pourcentage_moyen)];
  if (retard > 0) morceaux.push(`${pluriel(retard, 'programme')} en retard`);
  else morceaux.push('à jour');
  if (sans > 0) morceaux.push(`${sans} sans programme`);
  return { texte: morceaux.join(' · '), ton: retard > 0 ? 'text-amber-700' : 'text-emerald-700', barre: true };
}

/** La colonne « Appels de la semaine ». */
function resumeAppels(appels, espace) {
  const faits = nombre(appels?.faits);
  const attendus = nombre(appels?.attendus);
  const manquants = nombre(appels?.manquants);
  if (!attendus) return { texte: '-', ton: 'text-gray-500', note: 'Aucun cours passé' };
  return {
    texte: `${faits} / ${attendus}`,
    ton: manquants > 0 ? 'text-amber-700' : 'text-emerald-700',
    // Sans espace actif, l'enseignant ne PEUT pas faire l'appel : l'écran le
    // dit plutôt que de laisser croire à un oubli.
    note: estSansEspace(espace) ? 'sans espace actif' : null
  };
}

const SuiviEnseignants = () => {
  const [parametres, setParametres] = useSearchParams();
  const demandee = parametres.get('semaine');
  // Une semaine mal formée dans l'adresse est ignorée : semaine en cours.
  const semaine = EST_DATE.test(demandee || '') ? demandee : null;
  const filtre = FILTRES.some((f) => f.cle === parametres.get('filtre')) ? parametres.get('filtre') : 'tous';
  const recherche = parametres.get('q') || '';

  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const { estActive } = useFeatures();
  const { hasPermission } = usePermissions();

  // Seule la réponse de la DERNIÈRE demande s'affiche : en cliquant vite de
  // semaine en semaine, une réponse lente ne remplace pas la plus récente.
  const demande = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++demande.current;
    setErreur(null);
    try {
      const reponse = await suiviEnseignants.vueGlobale(semaine);
      if (numero === demande.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demande.current) setErreur(e.message);
    }
  }, [semaine]);

  useEffect(() => { charger(); }, [charger]);

  // `replace` : ni une frappe de recherche ni un changement de filtre ne
  // s'empile dans l'historique — « précédent » ramène d'où l'on venait.
  const changerParametre = (cle, valeur) => {
    const suivants = new URLSearchParams(parametres);
    if (valeur) suivants.set(cle, valeur);
    else suivants.delete(cle);
    setParametres(suivants, { replace: true });
  };
  const setRecherche = (texte) => changerParametre('q', texte);

  // Une bande repliee est une preference d'affichage, pas un filtre : elle
  // reste en memoire de la page et non dans l'adresse, qui porte deja la
  // semaine, le filtre et la recherche.
  //
  // TOUTES repliees a l'ouverture (decision du 11/10/2026) : la page s'ouvre
  // sur ses trois bandes et leurs nombres, et l'on deplie celle qu'on veut
  // lire. L'ecran tient alors dans sa hauteur, quel que soit l'effectif.
  const [repliees, setRepliees] = useState(() => new Set(ETATS_SUIVI.map((etat) => etat.cle)));
  const basculerBande = (cle) => setRepliees((avant) => {
    const apres = new Set(avant);
    if (apres.has(cle)) apres.delete(cle); else apres.add(cle);
    return apres;
  });

  const enseignants = useMemo(
    () => (Array.isArray(donnees?.enseignants) ? donnees.enseignants : []),
    [donnees]
  );

  const visibles = useMemo(() => {
    const garde = (FILTRES.find((f) => f.cle === filtre) || FILTRES[0]).garde;
    const cherche = sansAccents(recherche.trim());
    return enseignants.filter((e) => {
      if (!garde(e)) return false;
      if (!cherche) return true;
      const texte = sansAccents([
        e.nom, e.prenom, ...listeDe(e.matieres), ...listeDe(e.classes)
      ].join(' '));
      return texte.includes(cherche);
    });
  }, [enseignants, filtre, recherche]);

  // Les bandes, dans l'ordre : a reprendre, a surveiller, a jour. Une bande
  // vide ne s'affiche pas — une ecole ou tout va bien ne montre pas deux
  // bandeaux vides pour le dire.
  const groupes = useMemo(() => ETATS_SUIVI
    .map((etat) => ({ ...etat, lignes: visibles.filter((e) => etatSuivi(e) === etat.cle) }))
    .filter((g) => g.lignes.length > 0), [visibles]);

  if (!donnees) {
    return (
      <div className="w-full p-6">
        <EnTete semaine={null} choisie={semaine} onChoisir={(d) => changerParametre('semaine', d)} desactive />
        {/* Le premier chargement passe par le spinner du projet (LoadingSpinner,
            trente-quatre ecrans), et non par un rouage ecrit a la main. */}
        {erreur ? (
          <div role="alert" className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{erreur}</span>
            <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
          </div>
        ) : (
          <LoadingSpinner message="Chargement du suivi des enseignants…" size="lg" />
        )}
      </div>
    );
  }

  const compteurs = donnees.compteurs || {};
  const droits = donnees.droits || {};
  const voirAvis = Boolean(droits.voir_avis) && compteurs.avis_nouveaux !== null && compteurs.avis_nouveaux !== undefined;
  const nbEnseignants = nombre(compteurs.enseignants);
  const espacesActifs = nombre(compteurs.espaces_actifs);
  const appelsFaits = nombre(compteurs.appels_faits);
  const appelsAttendus = nombre(compteurs.appels_attendus);
  const appelsManquants = Math.max(0, appelsAttendus - appelsFaits);
  const enseignantsSansAppel = enseignants.filter((e) => nombre(e.appels?.manquants) > 0).length;
  const sansProgramme = nombre(compteurs.couples_sans_programme);
  // La vie scolaire, où se traitent les avis : même garde que la route
  // /administration/disciplinaire (porte ELEVES, licence vie scolaire), plus
  // l'espace enseignant qui porte l'onglet des avis.
  const peutOuvrirAvis = hasPermission('ELEVES') && estActive('module.vie_scolaire') && estActive('module.portail_enseignant');

  // Colonnes de la liste : la colonne « Avis » n'existe que pour qui peut les lire.
  const colonnes = voirAvis
    ? 'minmax(0,2.2fr) minmax(0,1.6fr) minmax(0,2fr) minmax(0,1.3fr) minmax(0,0.7fr) 132px 20px'
    : 'minmax(0,2.2fr) minmax(0,1.6fr) minmax(0,2fr) minmax(0,1.3fr) 132px 20px';

  const avecSemaine = semaine ? `?semaine=${semaine}` : '';

  return (
    <div className="w-full space-y-4 p-6">
      <EnTete
        semaine={donnees.semaine}
        choisie={semaine}
        onChoisir={(d) => changerParametre('semaine', d)}
      />

      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">La vue n'a pas pu être rafraîchie : {erreur}</span>
          <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <CarteCompteur
          libelle="Espaces enseignants actifs"
          icone={Users}
          couleur="from-[#246990] to-[#3084b5]"
          valeur={espacesActifs}
          sur={nbEnseignants}
          note={nbEnseignants - espacesActifs > 0
            ? `${pluriel(nbEnseignants - espacesActifs, 'enseignant')} sans espace actif`
            : 'Tous les enseignants ont leur espace'}
        />
        <CarteCompteur
          libelle="Appels faits"
          icone={CalendarCheck}
          couleur="from-[#c66d59] to-[#ae7467]"
          valeur={appelsFaits}
          sur={appelsAttendus}
          ton={appelsManquants > 0 ? 'attention' : (appelsAttendus > 0 ? 'bien' : 'neutre')}
          note={!appelsAttendus
            ? 'Aucun cours passé cette semaine'
            : appelsManquants > 0
              ? `${pluriel(appelsManquants, 'appel manquant', 'appels manquants')}, chez ${pluriel(enseignantsSansAppel, 'enseignant')}`
              : 'Aucun appel manquant'}
          complement={(
            <InfoBulle
              titre="Une estimation"
              ton="info"
              libelle="Comment les appels attendus sont comptés"
              iconeClassName="text-white/90"
            >
              Un appel est attendu pour chaque cours non annulé de la semaine, jusqu'à aujourd'hui, et pour chaque
              matinée où la classe d'un titulaire n'a pas de cours à l'emploi du temps. L'école n'a pas de calendrier
              des jours sans classe : un cours prévu un jour férié compte comme un appel manquant.
            </InfoBulle>
          )}
        />
        <CarteCompteur
          libelle="Programmes en retard"
          icone={ClipboardList}
          couleur="from-[#319190] to-[#63b5b4]"
          valeur={nombre(compteurs.programmes_en_retard)}
          sur={nombre(compteurs.couples)}
          ton={nombre(compteurs.programmes_en_retard) > 0 ? 'attention' : 'neutre'}
          note={sansProgramme > 0
            ? `${pluriel(sansProgramme, 'classe-matière', 'classes-matières')} sans programme saisi`
            : 'Chaque classe-matière a son programme'}
        />
        {voirAvis && (
          <CarteCompteur
            libelle="Avis des enseignants à lire"
            icone={MessageSquare}
            couleur="from-gray-800 to-gray-700"
            valeur={nombre(compteurs.avis_nouveaux)}
            note={peutOuvrirAvis ? (
              <Link to="/administration/disciplinaire?onglet=avis" className="font-semibold text-white underline-offset-2 hover:underline">
                Ouvrir dans la vie scolaire
              </Link>
            ) : 'Lus et traités par la vie scolaire'}
          />
        )}
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer les enseignants">
            {FILTRES.map((f) => {
              const actif = filtre === f.cle;
              const n = enseignants.filter(f.garde).length;
              return (
                <button
                  key={f.cle}
                  type="button"
                  aria-pressed={actif}
                  onClick={() => changerParametre('filtre', f.cle === 'tous' ? null : f.cle)}
                  className={classesFiltre(actif)}
                >
                  {f.libelle} ({n})
                </button>
              );
            })}
          </div>
          <label className="relative block lg:w-[260px]">
            <span className="sr-only">Rechercher un enseignant</span>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Enseignant, matière, classe"
              className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
            />
          </label>
        </div>

        {enseignants.length === 0 ? (
          <div className="p-4">
            <EtatVide
              icone={Users}
              titre="Aucun enseignant à suivre"
              description="Un enseignant apparaît ici dès qu'il a, pour l'année en cours, une classe, une matière ou des cours à l'emploi du temps, ou un espace enseignant."
            />
          </div>
        ) : visibles.length === 0 ? (
          <div className="p-4">
            <EtatVide
              compact
              icone={Users}
              titre="Aucun enseignant ne correspond"
              description="Changez de filtre ou de recherche."
              action={(
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const suivants = new URLSearchParams(parametres);
                    suivants.delete('q');
                    suivants.delete('filtre');
                    setParametres(suivants, { replace: true });
                  }}
                >
                  Voir tous les enseignants
                </Button>
              )}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[980px]">
              {/* En-têtes visuels : chaque ligne est un lien qui se lit en entier,
                  un rôle « table » lui ôterait sa nature de lien. */}
              <div
                aria-hidden="true"
                className="grid gap-3 border-b border-gray-200 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                style={{ gridTemplateColumns: colonnes }}
              >
                <span>Enseignant</span>
                <span>Classes</span>
                <span>Programme</span>
                <span>Appels de la semaine</span>
                {voirAvis && <span>Avis</span>}
                <span>Espace</span>
                <span />
              </div>
              {groupes.map((groupe) => (
                <div key={groupe.cle}>
                  {/* Colonnes FIXES : le chevron, le libelle et le nombre
                      tombent au meme endroit sur les trois bandes, quelle que
                      soit la longueur du libelle. En flex, « A jour » poussait
                      son nombre 60 px a gauche de celui de « A surveiller ». */}
                  <button
                    type="button"
                    onClick={() => basculerBande(groupe.cle)}
                    aria-expanded={!repliees.has(groupe.cle)}
                    className={`grid w-full items-center gap-2.5 border-b border-l-4 border-b-gray-200 px-4 py-2.5 text-left transition hover:brightness-[0.98] ${groupe.bande}`}
                    style={{ gridTemplateColumns: '16px 128px 34px minmax(0,1fr)' }}
                  >
                    <ChevronRight
                      size={15}
                      className={`shrink-0 transition-transform ${repliees.has(groupe.cle) ? '' : 'rotate-90'} ${groupe.texte}`}
                      aria-hidden="true"
                    />
                    <span className={`text-[13px] font-bold ${groupe.texte}`}>{groupe.libelle}</span>
                    <span className={`inline-flex h-[22px] min-w-[22px] items-center justify-center justify-self-start rounded-lg px-1.5 text-[11px] font-bold text-white ${groupe.pastille}`}>
                      {groupe.lignes.length}
                    </span>
                    <span className="truncate text-xs text-gray-600">{groupe.pourquoi}</span>
                  </button>

                  {!repliees.has(groupe.cle) && groupe.lignes.map((e) => {
                const programme = resumeProgramme(e.programme);
                const appels = resumeAppels(e.appels, e.espace);
                const matieres = listeDe(e.matieres);
                const classes = listeDe(e.classes);
                return (
                  <Link
                    key={e.id}
                    to={`/administration/enseignants/${e.id}${avecSemaine}`}
                    state={{ depuisListe: true }}
                    className="grid items-center gap-3 border-b border-gray-100 px-4 py-3 text-[13px] transition hover:bg-gray-50 focus:bg-[#eef7fb] focus:outline-none"
                    style={{ gridTemplateColumns: colonnes }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-gray-900">{nomPersonne(e) || 'Enseignant'}</span>
                      {matieres.length > 0 && <span className="block truncate text-xs text-gray-500">{matieres.join(', ')}</span>}
                    </span>
                    <span className="min-w-0 text-gray-700">{classes.join(', ') || '-'}</span>
                    <span className="min-w-0">
                      {programme.barre && (
                        <BarreAvancement
                          valeur={e.programme?.pourcentage_moyen}
                          libelle={`Avancement moyen des programmes de ${nomPersonne(e)}`}
                        />
                      )}
                      <span className={`mt-1 block text-xs ${programme.ton}`}>{programme.texte}</span>
                    </span>
                    <span>
                      <span className={`block font-semibold ${appels.ton}`}>{appels.texte}</span>
                      {appels.note && <span className="block text-[11px] text-gray-500">{appels.note}</span>}
                    </span>
                    {voirAvis && (
                      <span className="text-gray-700">
                        {e.avis_semaine === null || e.avis_semaine === undefined ? '-' : nombre(e.avis_semaine)}
                      </span>
                    )}
                    <span><BadgeEspace espace={e.espace} /></span>
                    <span className="flex text-gray-400"><ChevronRight className="h-4 w-4" /></span>
                  </Link>
                );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const EnTete = ({ semaine, choisie, onChoisir, desactive }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    {/* Ni fil d'Ariane ni sous-titre (decision du 11/10/2026) : la barre
        laterale dit deja ou l'on est, et le sous-titre repetait le titre. */}
    <h1 className="text-3xl font-bold text-[#246990]">Suivi des enseignants</h1>
    <ChoixSemaine semaine={semaine} choisie={choisie} onChoisir={onChoisir} desactive={desactive} />
  </div>
);

export default SuiviEnseignants;
